<template>
  <div class="page">
    <div class="logo_icon absolute top-10px left-50% transform -translate-x-1/2 z-999">
      <div class="flex items-center flex-col">
        <div class="w-38px">
          <img src="@/assets/images/home/logo.svg" alt="logo" class="w-full h-full" />
        </div>
        <div class="logo-text text-18px mt-1px ml-6px font-900">
          {{ JSON.parse(parsed || '{}').AppName || 'Aishmin' }}
        </div>
      </div>
    </div>
    <div class="login-page page" v-if="!isLogin">
      <div class="login-content mt-60%">
        <div class="bg-text w-100% h-97px">
          <img src="@/assets/images/login/bg_text.webp" alt="logo" class="w-100% h-100%" />
        </div>

        <div class="guest-up flex align-center relative mt-10px" @click="handleGuestUp">
          <div class="guest-up-img w-16px h-16px absolute left-30px top-12px">
            <img src="@/assets/images/login/guest_up.webp" alt="logo" />
          </div>
          <div class="guest-up-text">{{ t('login.guestMode') }}</div>
        </div>

        <div class="google-up flex align-center relative mt-10px" @click="handleGoogleUp">
          <div class="guest-up-img w-16px h-16px absolute left-30px top-12px">
            <img src="@/assets/images/login/google_up.webp" alt="logo" />
          </div>
          <div class="guest-up-text">{{ t('login.signInWithGoogle') }}</div>
        </div>

        <div class="sign-up flex align-center relative mt-10px" @click="handleRegister">
          <div class="guest-up-img w-16px h-16px absolute left-30px top-12px">
            <img src="@/assets/images/login/sign_up.webp" alt="logo" />
          </div>
          <div class="guest-up-text">{{ t('login.signUp') }}</div>
        </div>

        <div class="sign-in flex align-center relative mt-10px" @click="handleSignIn">
          <div class="guest-up-img w-16px h-16px absolute left-30px top-12px">
            <img src="@/assets/images/login/sign_up.webp" alt="logo" />
          </div>
          <div class="guest-up-text">Confirm</div>
        </div>

        <div class="sign-in-text text-center mt-10px">
          {{ t('login.bySigningUp') }} <br />
          the <span @click="router.push('/terms')">{{ t('login.userAgreement') }}</span>
          <span @click="router.push('/policy')">{{ t('login.privacyPolicy') }}</span>
        </div>
      </div>
    </div>
    <div >
      <sign-in @back="handleBack" @register="handleRegister" />
      <!-- <register v-if="isRegister" @back="handleBack" @signIn="handleSignIn" /> -->
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { theme } from '@/styles/theme'
import SignIn from './Component/signIn.vue'
import Register from './Component/Register.vue'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/stores/user'
import { useRouter } from 'vue-router'
import { googleTokenLogin } from 'vue3-google-login'
import CryptoJS from 'crypto-js'
import { showLoadingToast, closeToast, showSuccessToast, showErrorToast } from '@/utils/toast'
import { useWaterfallStore } from '@/stores/waterfall'

const { t } = useI18n()
const userStore = useUserStore()
const router = useRouter()
const waterfallStore = useWaterfallStore()
const parsed = localStorage.getItem('ai-chat-parsed')
const isSignIn = ref(false) // 显示登录组件
const isLogin = ref(false) // 是否登录
const isRegister = ref(false) // 显示注册组件

/**
 * 登录成功后刷新首页数据
 */
const refreshHomeData = async () => {
  try {
    // 刷新瀑布流数据
    await waterfallStore.refresh(0,waterfallStore.tabNavs[0].id)
  } catch (error) {
    console.error('Failed to refresh home data:', error)
  }
}

/**
 * 修复 iOS Safari 背景图不铺满问题
 */
const setAppHeight = () => {
  document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`)
}

onMounted(() => {
  setAppHeight()
  window.addEventListener('resize', setAppHeight)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', setAppHeight)
})

const handleSignIn = () => {
  isSignIn.value = true
  isLogin.value = true
  isRegister.value = false
}

const handleGoogleUp = async () => {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
  try {
    showLoadingToast(t('login.loggingIn'))
    const response = await googleTokenLogin({ clientId })
    const encryptedToken = CryptoJS.MD5(response.access_token).toString()
    await userStore.googleLogin(encryptedToken)

    // 刷新首页数据
    await refreshHomeData()

    router.push('/')
    showSuccessToast(t('login.loginSuccess'))
  } catch (error) {
    showErrorToast(error as string)
  } finally {
    // closeToast()
  }
}

const handleRegister = () => {
  isRegister.value = true
  isSignIn.value = false
  isLogin.value = true
}

const handleBack = () => {
  router.back()
}

const handleGuestUp = async () => {
  try {
    showLoadingToast(t('login.loggingIn'))
    await userStore.loginTouristFunc()
    await userStore.getUserInfo()

    // 刷新首页数据
    await refreshHomeData()

    router.push({ name: 'Home' })
    showSuccessToast(t('login.loginSuccess'))
  } catch (error) {
    showErrorToast(error as string)
    console.error('Guest login failed:', error)
  } finally {
    // closeToast()
  }
}

const goToPolicy = () => {
  router.push({ path: '/policy' })
}
onMounted(() => {
  const { isRegisterUrl, isSignInUrl, isLoginUrl } = router.currentRoute.value.query
  isRegister.value = isRegisterUrl === 'true'
  isSignIn.value = isSignInUrl === 'true'
  isLogin.value = isLoginUrl === 'true'
})
</script>

<style lang="scss" scoped>
.login-page {
  width: 100vw;
  height: var(--app-height); // 使用动态高度，解决 iOS Safari 100vh 问题
  min-height: 100vh;
  background: url('@/assets/images/login/bg.webp') no-repeat center center;
  background-size: cover;
  padding: 20px;

  .login-content {

    .guest-up,
    .google-up,
    .sign-up,
    .sign-in {
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 50px;
      border-radius: 32px;
      font-weight: 900;
      font-size: 15px;
    }

    .guest-up {
      background: linear-gradient(180deg, #ffffff 0%, #dacaff 100%);
      color: #333;
    }

    .google-up {
      background: linear-gradient(180deg, #9b75f3 0%, #6026cf 100%);
      color: #fff;
    }

    .sign-up {
      background: linear-gradient(#5a68ff, #5a68ff 0%, #95a5ff 100%);
      color: #fff;
    }

    .sign-in {
      background: v-bind('theme.colors.backgroundSecondary');
      color: #abb6ff;
    }

    .sign-in-text {
      font-weight: 500;
      font-size: 12px;
      color: v-bind('theme.colors.textPrimary');

      span {
        text-decoration: underline;
      }
    }
  }
}

.logo-text {
  background: linear-gradient(45.00000000000004deg, #7C9CFF 0%, #7058F8 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
</style>
